import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { withRouter } from 'react-router';

import { Notification } from '@xsolla/ui-core';
import { AlertExclamation, NewTab } from '@xsolla/ui-icons';

import { deleteAttributesSchema, getAttributesSchema, putAttributesSchema } from '../../../api';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { HorizontalCenterColumnBlock } from '../../../styled';
import { CodeEditor, FormFooter, HeaderTitle, LinkPAStyle, Notice, Preloader } from '../../ui';
import { CodeFieldTitle, CodeWrapper, ErrorWrapper, NotificationLinkWrapper } from './styles';
import { Props } from './types';
import { errorsMap, isStringJson, SCHEMA_EXAMPLE } from './utils';

const JSON_SCHEMA_DOC_LINK = 'https://json-schema.org/';

const AttributesSchemaForm = ({ history, currentProject }: Props): JSX.Element => {
  const { navigationPath } = usePaths();
  const intl = useIntl();

  const [schema, setSchema] = useState<string>('');
  const [schemaChanged, setSchemaChanged] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(true);
  const [submitFetching, setSubmitFetching] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  useEffect(() => {
    const getSchema = async () => {
      try {
        const currSchema = await getAttributesSchema(currentProject.id);

        setSchema(JSON.stringify(currSchema || SCHEMA_EXAMPLE, null, '\t'));
      } catch (err) {
        showErrorNotify(err);
      } finally {
        setFetching(false);
      }
    };

    getSchema();
  }, [currentProject.id]);

  const onSubmit = async () => {
    setSubmitError('');

    if (schema === '') {
      try {
        setSubmitFetching(true);

        await deleteAttributesSchema(currentProject.id);

        showSuccessNotify();
      } catch (err) {
        showErrorNotify(err);
      } finally {
        setSubmitFetching(false);
      }
    } else if (isStringJson(schema)) {
      try {
        setSubmitFetching(true);

        await putAttributesSchema(currentProject.id, JSON.parse(schema));

        showSuccessNotify();
      } catch (err) {
        if (err.code === '060-004' && err.details) {
          setSubmitError(errorsMap(intl, err));
        } else {
          showErrorNotify(err);
        }
      } finally {
        setSubmitFetching(false);
      }
    } else {
      setSubmitError(intl.formatMessage({ id: 'app.validation.json-schema' }));
    }
  };

  const backToNavigation = () =>
    currentProject?.id && history.push({ pathname: navigationPath(currentProject.id) });

  const renderNotificationLink = () => {
    return (
      <NotificationLinkWrapper>
        <FormattedMessage
          id="user-data-base.settings.attributes.notification-link"
          values={{
            a: (chunk: string) => (
              <LinkPAStyle href={JSON_SCHEMA_DOC_LINK} target="_blank">
                {chunk}
                <NewTab />
              </LinkPAStyle>
            )
          }}
        />
      </NotificationLinkWrapper>
    );
  };

  if (fetching) {
    return <Preloader />;
  }

  return (
    <>
      <HorizontalCenterColumnBlock>
        <HeaderTitle
          title={intl.formatMessage({ id: 'user-data-base.settings.attributes.title' })}
          subtitle={intl.formatMessage(
            { id: 'user-data-base.settings.attributes.subtitle' },
            { br: () => <br /> }
          )}
          notification={intl.formatMessage(
            {
              id: 'user-data-base.settings.attributes.notification'
            },
            {
              ul: (chunk: string) => (
                <ul style={{ padding: '0 0 0 24px', margin: '0' }}>{chunk}</ul>
              ),
              li: (chunk: string) => <li>{chunk}</li>
            }
          )}
          notificationLink={renderNotificationLink()}
          notificationIcon={Notice}
        />
      </HorizontalCenterColumnBlock>

      <CodeFieldTitle>
        {intl.formatMessage({ id: 'user-data-base.settings.attributes.field.title' })}
      </CodeFieldTitle>
      <CodeWrapper data-test-id="user-attributes-code-editor">
        <CodeEditor
          initialValue={schema.toString()}
          onChange={(value) => {
            if (!schemaChanged) {
              setSchemaChanged(true);
            }
            setSchema(value);
          }}
          mode="javascript"
        />
      </CodeWrapper>

      {submitError && (
        <ErrorWrapper>
          <Notification palette="danger" icon={AlertExclamation} caption={submitError}>
            {intl.formatMessage({ id: 'user-data-base.settings.attributes.error.title' })}
          </Notification>
        </ErrorWrapper>
      )}

      <FormFooter
        onSubmit={onSubmit}
        onCancel={backToNavigation}
        disabled={!schemaChanged}
        fetching={submitFetching}
      />
    </>
  );
};

export default withRouter(AttributesSchemaForm);
